/**
 * useTickStream
 *
 * Tick subscriptions and analysis event handling — fully delegated to the
 * multiplexed WebSocket singleton (wsClient → /ws). The legacy `/ws/market`
 * connection is gone; analysis updates now flow through the `analysis` topic
 * (Redis channel `analysis:<userId>`) and the deprecated `check_analysis` WS
 * RPC was replaced by the REST endpoint `GET /api/analysis/:id/status`.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { wsClient } from '../lib/ws/client.js';
import type { TopicHandler } from '../lib/ws/client.js';
import { TOPIC_ANALYSIS } from '@trading/shared';
import api from '../services/api';

interface Tick {
  symbol: string;
  price: number;
  timestamp: number;
  bid?: number;
  ask?: number;
  /** Provider that emitted this tick. Consumers must match this against the
   *  active candle source before applying the tick — see Analysis.tsx onTick. */
  source?: 'twelvedata' | 'binance';
}

interface AnalysisUpdate {
  type: 'processing' | 'completed' | 'error' | 'status';
  analysisId: string;
  progress?: number;
  durationMs?: number;
  aiModel?: string;
  status?: 'pending' | 'completed' | 'failed' | 'stale' | 'not_found' | 'error';
  message?: string;
  result?: {
    setup: {
      valid: boolean;
      direction: 'LONG' | 'SHORT' | null;
      confidence: number;
      entry?: { price: number; type: string; reasoning: string } | null;
      stopLoss?: { price: number; reasoning: string } | null;
      targets?: {
        t1: { price: number; reasoning: string };
        t2: { price: number; reasoning: string };
        t3: { price: number; reasoning: string };
      } | null;
      riskReward?: number | null;
      riskPercent?: number | null;
      invalidation?: string | null;
    };
    analysis: {
      trend: 'bullish' | 'bearish' | 'ranging';
      trendStrength: number;
      marketPhase: string;
      bias: 'long' | 'short' | 'neutral';
    };
    structure: unknown;
    zones: unknown;
    narrative: string;
    chartMarkers?: unknown[];
  };
  error?: string;
}

interface AnalysisStatusResponse {
  type: 'analysis_status';
  analysisId: string;
  status: 'pending' | 'completed' | 'no_setup' | 'failed' | 'stale' | 'not_found' | 'error';
  message?: string;
  result?: AnalysisUpdate['result'];
  error?: string;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

interface UseTickStreamOptions {
  onTick?: (tick: Tick) => void;
  onAnalysisUpdate?: (update: AnalysisUpdate) => void;
  onAnalysisStatus?: (response: AnalysisStatusResponse) => void;
  onReconnect?: () => void;
  autoReconnect?: boolean;
}

export function useTickStream(options: UseTickStreamOptions = {}) {
  const { onTick, onAnalysisUpdate, onAnalysisStatus, onReconnect } = options;

  const [connected, setConnected] = useState(wsClient.isConnected());
  const [lastTick, setLastTick] = useState<Tick | null>(null);
  const [subscribedSymbols, setSubscribedSymbols] = useState<Set<string>>(new Set());

  // Callback refs (kept current without retriggering effects)
  const onTickRef = useRef(onTick);
  const onAnalysisUpdateRef = useRef(onAnalysisUpdate);
  const onAnalysisStatusRef = useRef(onAnalysisStatus);
  const onReconnectRef = useRef(onReconnect);
  useEffect(() => { onTickRef.current = onTick; }, [onTick]);
  useEffect(() => { onAnalysisUpdateRef.current = onAnalysisUpdate; }, [onAnalysisUpdate]);
  useEffect(() => { onAnalysisStatusRef.current = onAnalysisStatus; }, [onAnalysisStatus]);
  useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);

  // Stable handler identities for wsClient subscriptions.
  const tickHandlerRef = useRef<TopicHandler>((data) => {
    const tick = data as Tick;
    setLastTick(tick);
    onTickRef.current?.(tick);
  });

  const analysisHandlerRef = useRef<TopicHandler>((data) => {
    onAnalysisUpdateRef.current?.(data as AnalysisUpdate);
  });

  // Map of symbol → cleanup function returned by wsClient.subscribe
  const tickCleanupRef = useRef<Map<string, () => void>>(new Map());

  const subscribe = useCallback((symbol: string) => {
    setSubscribedSymbols((prev) => new Set(prev).add(symbol));
    if (!tickCleanupRef.current.has(symbol)) {
      const unsub = wsClient.subscribe(`ticks:${symbol}`, tickHandlerRef.current);
      tickCleanupRef.current.set(symbol, unsub);
    }
  }, []);

  const unsubscribe = useCallback((symbol: string) => {
    setSubscribedSymbols((prev) => {
      const next = new Set(prev);
      next.delete(symbol);
      return next;
    });
    const unsub = tickCleanupRef.current.get(symbol);
    if (unsub) {
      unsub();
      tickCleanupRef.current.delete(symbol);
    }
  }, []);

  // checkAnalysis was a WS RPC on /ws/market. Replaced by REST GET /:id/status.
  const checkAnalysis = useCallback(async (analysisId: string) => {
    try {
      const response = await api.get<AnalysisStatusResponse>(
        `/analysis/${analysisId}/status`,
      );
      onAnalysisStatusRef.current?.(response.data);
    } catch {
      onAnalysisStatusRef.current?.({
        type: 'analysis_status',
        analysisId,
        status: 'error',
      });
    }
  }, []);

  // Subscribe to analysis topic + onReconnect listener for the lifetime of the hook.
  useEffect(() => {
    const unsubAnalysis = wsClient.subscribe(TOPIC_ANALYSIS, analysisHandlerRef.current);
    const offReconnect = wsClient.onReconnect(() => {
      onReconnectRef.current?.();
    });

    // Poll connected state — the singleton doesn't expose change events, so we
    // sync once per second. Cheap and adequate for UI badge purposes.
    setConnected(wsClient.isConnected());
    const interval = setInterval(() => {
      const next = wsClient.isConnected();
      setConnected((curr) => (curr === next ? curr : next));
    }, 1_000);

    const tickCleanups = tickCleanupRef.current;

    return () => {
      unsubAnalysis();
      offReconnect();
      clearInterval(interval);
      for (const unsub of tickCleanups.values()) unsub();
      tickCleanups.clear();
    };
  }, []);

  return {
    connected,
    authenticated: connected,
    connectionStatus: (connected ? 'connected' : 'connecting') as ConnectionStatus,
    lastTick,
    subscribedSymbols: Array.from(subscribedSymbols),
    subscribe,
    unsubscribe,
    checkAnalysis,
  };
}

export default useTickStream;
